import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ShieldCheck } from "lucide-react";
import { pdf, Document, Page, StyleSheet, Text, View, Image } from "@react-pdf/renderer";
import { certificatesApi } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const pdfStyles = StyleSheet.create({
    page: {
        backgroundColor: "#F5F5F4",
        padding: 36,
        fontFamily: "Helvetica",
    },
    frame: {
        flex: 1,
        border: "2px solid #B8892C",
        padding: 28,
    },
    innerFrame: {
        flex: 1,
        border: "1px solid #1a1a1a",
        padding: 28,
        justifyContent: "space-between",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    brandBlock: { flexDirection: "row", alignItems: "center", gap: 12 },
    brandTile: {
        width: 44,
        height: 44,
        backgroundColor: "#1a1a1a",
        color: "#B8892C",
        fontFamily: "Helvetica-Bold",
        fontSize: 26,
        textAlign: "center",
        paddingTop: 6,
    },
    brandName: { fontFamily: "Helvetica-Bold", fontSize: 22, letterSpacing: 2 },
    brandTag: { fontSize: 8, color: "#374151", letterSpacing: 3, marginTop: 2 },
    certKind: {
        fontSize: 10,
        color: "#B8892C",
        letterSpacing: 3,
        textAlign: "right",
    },
    title: {
        fontFamily: "Helvetica-Bold",
        fontSize: 32,
        marginTop: 10,
        letterSpacing: 1,
        color: "#1a1a1a",
    },
    orangeRule: {
        height: 3,
        width: 60,
        backgroundColor: "#B8892C",
        marginTop: 10,
        marginBottom: 24,
    },
    line: { fontSize: 12, color: "#374151", lineHeight: 1.6 },
    holderName: {
        fontFamily: "Helvetica-Bold",
        fontSize: 32,
        color: "#1a1a1a",
        marginTop: 14,
        marginBottom: 14,
        letterSpacing: 2,
    },
    courseTitle: {
        fontFamily: "Helvetica-Bold",
        fontSize: 20,
        color: "#B8892C",
        marginTop: 8,
        letterSpacing: 1,
    },
    footer: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-end",
        marginTop: 24,
    },
    meta: { fontSize: 9, color: "#374151", letterSpacing: 2, lineHeight: 1.6 },
    metaLabel: { fontSize: 8, color: "#374151", letterSpacing: 3, marginTop: 6 },
    metaValue: { fontFamily: "Helvetica-Bold", fontSize: 12, color: "#1a1a1a" },
    signBlock: { alignItems: "center", gap: 6, marginRight: 40 },
    signLine: {
        borderBottom: "1px solid #1a1a1a",
        width: 160,
        height: 28,
    },
    signLabel: {
        fontSize: 8,
        color: "#374151",
        letterSpacing: 3,
        marginTop: 4,
    },
    qrBlock: { alignItems: "center" },
    qr: { width: 90, height: 90 },
    verifyLabel: { fontSize: 7, color: "#374151", marginTop: 4, letterSpacing: 2 },
});

function CertificateDocument({ cert }) {
    const issuedFormatted = new Date(cert.issued_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const logoUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}/brand/logo-horizontal-dark.png?v=3`
            : "/brand/logo-horizontal-dark.png?v=3";
    return (
        <Document>
            <Page size="A4" orientation="landscape" style={pdfStyles.page}>
                <View style={pdfStyles.frame}>
                    <View style={pdfStyles.innerFrame}>
                        <View style={pdfStyles.header}>
                            <View style={pdfStyles.brandBlock}>
                                <Image
                                    src={logoUrl}
                                    style={{ width: 130, height: 32 }}
                                />
                                <Text style={{ ...pdfStyles.brandTag, marginLeft: 12, alignSelf: "center" }}>
                                    TRAINING ACADEMY
                                </Text>
                            </View>
                            <View>
                                <Text style={pdfStyles.certKind}>CERTIFICATE OF COMPLETION</Text>
                                <Text style={{ ...pdfStyles.certKind, marginTop: 6, color: "#374151" }}>
                                    {cert.cert_number}
                                </Text>
                            </View>
                        </View>

                        <View>
                            <Text style={pdfStyles.title}>THIS CERTIFIES THAT</Text>
                            <View style={pdfStyles.orangeRule} />
                            <Text style={pdfStyles.holderName}>{cert.holder_name.toUpperCase()}</Text>
                            <Text style={pdfStyles.line}>
                                has successfully completed the EB Construction Training Academy programme:
                            </Text>
                            <Text style={pdfStyles.courseTitle}>{cert.course_title}</Text>
                            <Text style={{ ...pdfStyles.line, marginTop: 12 }}>
                                Awarded on {issuedFormatted}. This certificate is publicly verifiable at the URL below and by scanning the QR code.
                            </Text>
                            <Text style={{ ...pdfStyles.line, marginTop: 10, fontStyle: "italic", color: "#B8892C" }}>
                                Built on Trust. Designed for Life.
                            </Text>
                        </View>

                        <View style={pdfStyles.footer}>
                            <View style={pdfStyles.meta}>
                                <Text style={pdfStyles.metaLabel}>ISSUED</Text>
                                <Text style={pdfStyles.metaValue}>{issuedFormatted}</Text>
                                <Text style={pdfStyles.metaLabel}>CERTIFICATE NO.</Text>
                                <Text style={pdfStyles.metaValue}>{cert.cert_number}</Text>
                                <Text style={pdfStyles.metaLabel}>VERIFY AT</Text>
                                <Text style={{ ...pdfStyles.line, fontSize: 9 }}>
                                    {cert.verification_url}
                                </Text>
                            </View>
                            <View style={pdfStyles.signBlock}>
                                <View style={pdfStyles.signLine} />
                                <Text style={pdfStyles.signLabel}>DIRECTOR · ELEVATEBUILD</Text>
                                <Text style={{ ...pdfStyles.signLabel, marginTop: 2 }}>
                                    CO. NO. {COMPANY.companyNumber}
                                </Text>
                            </View>
                            <View style={pdfStyles.qrBlock}>
                                <Image src={cert.qr_data_url} style={pdfStyles.qr} />
                                <Text style={pdfStyles.verifyLabel}>SCAN TO VERIFY</Text>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
}

export default function CertificateDetailPage() {
    const { certId } = useParams();
    const navigate = useNavigate();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        setLoading(true);
        certificatesApi
            .get(certId)
            .then(setCert)
            .catch((e) => {
                const st = e?.response?.status;
                if (st === 403) setError("forbidden");
                else if (st === 404) setError("not_found");
                else setError("unknown");
            })
            .finally(() => setLoading(false));
    }, [certId]);

    const downloadPdf = async () => {
        if (!cert || downloading) return;
        setDownloading(true);
        try {
            const blob = await pdf(<CertificateDocument cert={cert} />).toBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ElevateBuild-Cert-${cert.cert_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div
                className="grid min-h-[calc(100vh-72px)] place-items-center bg-brand-offwhite font-mono-tag text-brand-steel"
                data-testid="certificate-loading"
            >
                LOADING CERTIFICATE…
            </div>
        );
    }
    if (error === "not_found" || error === "forbidden") {
        return (
            <div className="container-x py-40" data-testid="certificate-not-found">
                <p className="font-mono-tag text-brand-gold">
                    {error === "forbidden" ? "ACCESS DENIED" : "NOT FOUND"}
                </p>
                <h1 className="mt-6 font-display text-6xl">
                    {error === "forbidden" ? "NOT YOUR CERTIFICATE." : "CERTIFICATE NOT FOUND."}
                </h1>
                <button
                    type="button"
                    onClick={() => navigate("/portal/certificates")}
                    className="mt-8 btn-primary"
                >
                    Back to certificates
                </button>
            </div>
        );
    }
    if (!cert) return null;

    const issuedFormatted = new Date(cert.issued_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <div className="bg-brand-offwhite" data-testid="certificate-detail-page">
            <section className="border-b border-brand-line py-8">
                <div className="container-x flex flex-wrap items-center justify-between gap-4">
                    <Link
                        to="/portal/certificates"
                        className="inline-flex items-center gap-2 font-mono-tag text-brand-steel hover:text-brand-gold"
                        data-testid="certificate-back"
                    >
                        <ArrowLeft className="h-3 w-3" />
                        All certificates
                    </Link>
                    <button
                        type="button"
                        onClick={downloadPdf}
                        disabled={downloading}
                        className="btn-primary disabled:opacity-60"
                        data-testid="certificate-download-pdf"
                    >
                        {downloading ? "Preparing PDF…" : "Download PDF"}
                        <Download className="h-4 w-4" />
                    </button>
                </div>
            </section>

            {/* Certificate visual (screen version) */}
            <section className="py-16">
                <div className="container-x">
                    <div
                        className="mx-auto max-w-5xl border-4 border-brand-gold bg-brand-offwhite p-6 md:p-10"
                        data-testid="certificate-visual"
                    >
                        <div className="border border-brand-charcoal p-8 md:p-12">
                            <div className="flex flex-col items-start justify-between gap-4 border-b border-brand-charcoal/20 pb-6 md:flex-row md:items-center">
                                <div className="flex items-center gap-3">
                                    <span className="grid h-11 w-11 place-items-center bg-brand-charcoal font-display text-3xl leading-[1.1] text-brand-gold">
                                        E
                                    </span>
                                    <div>
                                        <p className="font-display text-2xl tracking-wide">
                                            ELEVATEBUILD
                                        </p>
                                        <p className="font-mono-tag text-brand-steel">
                                            CONSTRUCTION SVC · TRAINING ACADEMY
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono-tag text-brand-gold">
                                        CERTIFICATE OF COMPLETION
                                    </p>
                                    <p className="mt-1 font-mono-tag text-brand-steel">
                                        {cert.cert_number}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-10">
                                <p className="font-display text-5xl tracking-tight md:text-6xl">
                                    THIS CERTIFIES THAT
                                </p>
                                <div className="mt-3 h-[3px] w-16 bg-brand-gold" />
                                <p
                                    className="mt-6 font-display text-5xl uppercase leading-tight md:text-6xl"
                                    data-testid="certificate-holder-name"
                                >
                                    {cert.holder_name}
                                </p>
                                <p className="mt-4 font-body text-lg text-brand-steel">
                                    has successfully completed the ElevateBuild Training Academy programme
                                </p>
                                <p
                                    className="mt-2 font-display text-3xl text-brand-gold md:text-4xl"
                                    data-testid="certificate-course-title"
                                >
                                    {cert.course_title}
                                </p>
                                <p className="mt-4 font-body text-base text-brand-steel">
                                    Awarded on {issuedFormatted}. This certificate is publicly
                                    verifiable at the URL below and by scanning the QR code.
                                </p>
                            </div>

                            <div className="mt-10 flex flex-col items-start justify-between gap-8 border-t border-brand-charcoal/20 pt-6 md:flex-row md:items-end">
                                <div>
                                    <p className="font-mono-tag text-brand-steel">ISSUED</p>
                                    <p className="mt-1 font-display text-xl">{issuedFormatted}</p>
                                    <p className="mt-4 font-mono-tag text-brand-steel">VERIFY AT</p>
                                    <p
                                        className="mt-1 break-all font-body text-sm text-brand-charcoal"
                                        data-testid="certificate-verification-url"
                                    >
                                        {cert.verification_url}
                                    </p>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <div className="h-10 w-40 border-b border-brand-charcoal" />
                                    <p className="font-mono-tag text-brand-steel">
                                        DIRECTOR · ELEVATEBUILD
                                    </p>
                                    <p className="font-mono-tag text-brand-steel">
                                        CO. NO. {COMPANY.companyNumber}
                                    </p>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <img
                                        src={cert.qr_data_url}
                                        alt="Verification QR code"
                                        className="h-28 w-28"
                                        data-testid="certificate-qr"
                                    />
                                    <p className="font-mono-tag text-brand-steel">
                                        SCAN TO VERIFY
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t border-brand-line pb-16">
                <div className="container-x">
                    <div className="mx-auto flex max-w-3xl items-start gap-4 border-l-2 border-brand-gold bg-white p-6">
                        <ShieldCheck className="mt-1 h-5 w-5 flex-shrink-0 text-brand-gold" />
                        <div>
                            <p className="font-mono-tag text-brand-steel">
                                THIRD-PARTY VERIFICATION
                            </p>
                            <p className="mt-2 font-body text-brand-charcoal">
                                Anyone with the verification URL can confirm this certificate at{" "}
                                <Link
                                    to={`/verify/${cert.verification_slug}`}
                                    className="text-brand-gold underline-offset-4 hover:underline"
                                >
                                    /verify/{cert.verification_slug}
                                </Link>{" "}
                                — no login required.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
